/**
 * Labels, colors, sizes, and assets for the gamification leaderboard and level badges.
 */
import type { PodiumRank, GamificationLevelBadgeTier } from '@/types/gamification.types';
import bronzePng from '@/assets/badges/bronze.png';
import silverPng from '@/assets/badges/silver.png';
import goldPng from '@/assets/badges/gold.png';

/** Page size shared by both leaderboard tabs. */
export const LEADERBOARD_PAGE_SIZE = 20;

/** Debounce before a leaderboard search hits the API. */
export const LEADERBOARD_SEARCH_DEBOUNCE_MS = 350;

/** Visual order: silver, gold, bronze (center is tallest). */
export const PODIUM_ORDER: PodiumRank[] = [2, 1, 3];

/** Badge size per rank — gold is largest. */
export const PODIUM_BADGE_SIZE: Record<PodiumRank, 'lg' | 'xl' | '2xl'> = {
  1: '2xl',
  2: 'xl',
  3: 'lg',
};

/** Card min-height per rank so gold stands taller. */
export const PODIUM_SIZE_CLASS: Record<PodiumRank, string> = {
  1: 'min-h-[168px] p-3 sm:min-h-[176px]',
  2: 'min-h-[138px] p-3 sm:min-h-[146px]',
  3: 'min-h-[112px] p-3 sm:min-h-[120px]',
};

/** Rank-colored ring on each podium card. */
export const PODIUM_RING_CLASS: Record<PodiumRank, string> = {
  1: 'ring-2 ring-amber-400/40 z-10',
  2: 'ring-2 ring-slate-300/60',
  3: 'ring-2 ring-orange-400/40',
};

/** Podium list layout before the column count is applied. */
export const PODIUM_LIST_CLASS = 'm-0 list-none p-0 grid items-end gap-2 sm:gap-3';

/** Column layout for 1, 2, or 3 filled podium slots. */
export const PODIUM_COLUMN_CLASS: Record<1 | 2 | 3, string> = {
  1: 'grid-cols-1 max-w-xs mx-auto',
  2: 'grid-cols-2 max-w-md mx-auto',
  3: 'grid-cols-3',
};

/** Shared chrome of a podium card. */
export const PODIUM_CARD_CLASS =
  'flex w-full flex-col items-center justify-center rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md';

/** Placeholder cell when a podium rank is empty on a wider grid. */
export const PODIUM_EMPTY_SLOT_CLASS = 'hidden sm:block';

/** Gold / silver / bronze chip colors for ranks 1–3. */
export interface LeaderboardRankStyle {
  ring: string;
  bg: string;
  icon: string;
  label: string;
}

/** Gold / silver / bronze ring and icon colors for ranks 1–3. */
export const LEADERBOARD_RANK_STYLES: Record<PodiumRank, LeaderboardRankStyle> = {
  1: {
    ring: 'ring-amber-400/60',
    bg: 'bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-950/40 dark:to-amber-900/20',
    icon: 'text-amber-500',
    label: 'Or',
  },
  2: {
    ring: 'ring-slate-300/70',
    bg: 'bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800/50 dark:to-slate-900/30',
    icon: 'text-slate-400',
    label: 'Argent',
  },
  3: {
    ring: 'ring-orange-400/50',
    bg: 'bg-gradient-to-br from-orange-100 to-orange-50 dark:from-orange-950/30 dark:to-orange-900/15',
    icon: 'text-orange-600',
    label: 'Bronze',
  },
};

/** Badge box / icon / text scale per size. */
export const LEADERBOARD_RANK_SIZE = {
  sm: { box: 'h-8 w-8', icon: 16, text: 'text-xs' },
  md: { box: 'h-9 w-9', icon: 18, text: 'text-sm' },
  lg: { box: 'h-12 w-12', icon: 24, text: 'text-base' },
  xl: { box: 'h-14 w-14', icon: 28, text: 'text-base' },
  '2xl': { box: 'h-16 w-16', icon: 32, text: 'text-lg' },
} as const;

/** Size token for {@link LEADERBOARD_RANK_SIZE}. */
export type LeaderboardRankSize = keyof typeof LEADERBOARD_RANK_SIZE;

/** Avatar box size + radius per size. */
export const TUTOR_AVATAR_BOX_CLASS = {
  sm: 'h-8 w-8 rounded-lg',
  md: 'h-9 w-9 rounded-lg',
  lg: 'h-12 w-12 rounded-xl',
} as const;

/** Size token for {@link TUTOR_AVATAR_BOX_CLASS}. */
export type TutorAvatarSize = keyof typeof TUTOR_AVATAR_BOX_CLASS;

/** Placeholder user-icon size matching {@link TUTOR_AVATAR_BOX_CLASS}. */
export const TUTOR_AVATAR_ICON_CLASS: Record<TutorAvatarSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

/** Shared row shell for tutor and establishment leaderboard lines. */
export const LEADERBOARD_ROW_CLASS =
  'flex items-center gap-3 rounded-xl border bg-card px-4 transition-colors';

/** Highlight ring when the row is the viewer’s own entry. */
export const LEADERBOARD_ROW_HIGHLIGHT_CLASS =
  'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_8%,transparent)] ring-1 ring-[var(--color-2-nuance-1)]/30';

/** Establishment icon box size, compact vs default. */
export const ESTABLISHMENT_ICON_BOX_CLASS = {
  compact: 'h-8 w-8',
  default: 'h-9 w-9',
} as const;

/** Establishment icon glyph size, compact vs default. */
export const ESTABLISHMENT_ICON_CLASS = {
  compact: 'h-4 w-4',
  default: 'h-5 w-5',
} as const;

/** Bundled PNG src for each gamification badge tier. */
export const GAMIFICATION_BADGE_ASSETS: Record<GamificationLevelBadgeTier, string> = {
  bronze: bronzePng,
  silver: silverPng,
  gold: goldPng,
};

/** UI label for a badge tier. */
export const GAMIFICATION_TIER_LABELS: Record<GamificationLevelBadgeTier, string> = {
  bronze: 'Bronze',
  silver: 'Argent',
  gold: 'Gold',
};
